interface TokenData {
  access_token: string;
  refresh_token: string;
  expires_at?: string;
}

export class TokenStorage {
  private static readonly ACCESS_TOKEN_KEY = 'adminToken';
  private static readonly REFRESH_TOKEN_KEY = 'adminRefreshToken';
  private static readonly TOKEN_EXPIRES_KEY = 'adminTokenExpires';
  private static readonly USER_DATA_KEY = 'adminUser';

  static setTokens(tokens: TokenData): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);
    
    if (tokens.expires_at) {
      localStorage.setItem(this.TOKEN_EXPIRES_KEY, tokens.expires_at);
    }
  }

  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static getTokenExpiration(): string | null {
    return localStorage.getItem(this.TOKEN_EXPIRES_KEY);
  }

  static hasTokens(): boolean {
    return !!(this.getAccessToken() && this.getRefreshToken());
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRES_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
  }

  static isAccessTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    if (!expiration) return true;

    try {
      const expirationDate = new Date(expiration);
      const now = new Date();
      return now >= expirationDate;
    } catch (error) {
      console.error('Error checking access token expiration:', error);
      return true;
    }
  }

  static getTimeUntilExpiration(): number | null {
    const expiration = this.getTokenExpiration();
    if (!expiration) return null;

    try {
      const expirationDate = new Date(expiration);
      const now = new Date();
      return expirationDate.getTime() - now.getTime();
    } catch (error) {
      console.error('Error calculating time until expiration:', error);
      return null;
    }
  }

  static willExpireSoon(thresholdMinutes: number = 5): boolean {
    const timeUntilExpiration = this.getTimeUntilExpiration();
    if (timeUntilExpiration === null) return true;
    
    return timeUntilExpiration <= thresholdMinutes * 60 * 1000;
  }

  static cleanupExpiredTokens(): void {
    if (this.isAccessTokenExpired()) {
      this.clearTokens();
    }
  }
} 